/**
 * CustomFieldsForm — renderiza un conjunto de campos personalizados (GlobalCustomField)
 * y produce/edita el array `personalizados` del documento.
 *
 * Estructura de cada valor: { tipo, nombre, valor, [valor_label, link_entity] }.
 * Para tipo 'entity_link', `valor` = id de la entidad y `valor_label` = texto a mostrar.
 *
 * Reutilizable en compras (contabilizar intl, alta/edición de factura) y, a futuro,
 * podría reemplazar la lógica inline de ventas.
 */
import DatePicker from '@/components/DatePicker'
import EntityLinkPicker, { type LinkEntity } from '@/components/EntityLinkPicker'
import { inputCls, labelCls } from '@/lib/formStyles'
import { MultiSelectField } from '@/components/ds/MultiSelectField'
import type { GlobalCustomField } from '@/modules/config/hooks/useConfig'

export interface Personalizado {
  tipo: string
  nombre: string
  valor: string
  valor_label?: string
  link_entity?: string
}

interface Props {
  fields: GlobalCustomField[]
  value: Personalizado[]
  onChange: (next: Personalizado[]) => void
  disabled?: boolean
}

/**
 * Cruza las definiciones del tenant con los valores guardados en el documento.
 *
 * El JSON `personalizados` guarda **solo lo que se llenó**, así que un documento
 * emitido antes de que existiera un campo —o cuyo autor lo dejó vacío— no lo
 * tiene, y una UI que recorra el JSON nunca lo muestra: el usuario no tiene
 * dónde escribirlo. La fuente de la lista tienen que ser las DEFINICIONES.
 *
 * Devuelve un campo por definición activa (con su valor guardado, o vacío) y
 * después los que existen en el documento pero ya no están definidos (ad-hoc o
 * campos desactivados), para no esconder datos que alguien cargó.
 */
export function mergeCustomFields(
  fields: GlobalCustomField[],
  values: Personalizado[] | null | undefined,
): Personalizado[] {
  const guardados = (values ?? []).filter(p => p && typeof p === 'object')
  const porNombre = new Map(guardados.map(p => [p.nombre, p]))
  const definidos = fields.map<Personalizado>(f => {
    const cur = porNombre.get(f.nombre)
    return {
      tipo: f.tipo,
      nombre: f.nombre,
      valor: cur?.valor ?? '',
      ...(cur?.valor_label ? { valor_label: cur.valor_label } : {}),
      ...(f.link_entity ? { link_entity: f.link_entity } : {}),
    }
  })
  const nombresDefinidos = new Set(fields.map(f => f.nombre))
  const extras = guardados.filter(p => !nombresDefinidos.has(p.nombre))
  return [...definidos, ...extras]
}

export default function CustomFieldsForm({ fields, value, onChange, disabled }: Props) {
  const byName = new Map(value.map(v => [v.nombre, v]))

  const setField = (f: GlobalCustomField, patch: Partial<Personalizado>) => {
    const next = [...value]
    const idx = next.findIndex(v => v.nombre === f.nombre)
    const base: Personalizado = idx >= 0
      ? { ...next[idx] }
      : { tipo: f.tipo, nombre: f.nombre, valor: '' }
    base.tipo = f.tipo
    if (f.tipo === 'entity_link') base.link_entity = f.link_entity
    const merged = { ...base, ...patch }
    if (idx >= 0) next[idx] = merged
    else next.push(merged)
    onChange(next)
  }

  if (!fields.length) return null

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map(f => {
        const cur = byName.get(f.nombre)
        const valor = cur?.valor ?? (f.tipo === 'entity_link' ? '' : (f.default_value || ''))
        return (
          <div key={f.id} className={f.tipo === 'entity_link' ? 'sm:col-span-2' : ''}>
            <label className={labelCls}>
              {f.nombre}{f.is_required && <span className="text-danger-solid ml-0.5">*</span>}
            </label>

            {f.tipo === 'entity_link' && f.link_entity ? (
              <EntityLinkPicker aria-label={f.nombre}
                linkEntity={f.link_entity as LinkEntity}
                valueId={valor}
                valueLabel={cur?.valor_label || ''}
                onChange={(id, label) => setField(f, { valor: id, valor_label: label })}
                required={f.is_required}
                disabled={disabled}
              />
            ) : f.tipo === 'multiselect' ? (
              // Se guarda «A; B» (#1752): lo que imprime o exporta el valor como
              // texto sigue igual, y el reporte separa las opciones al filtrar.
              <MultiSelectField
                dense
                aria-label={f.nombre}
                options={(f.opciones || []).map(o => ({ value: o, label: o }))}
                value={valor ? valor.split(';').map(x => x.trim()).filter(Boolean) : []}
                onChange={(sel: string[]) => setField(f, { valor: sel.join('; ') })}
                placeholder="Seleccionar…"
                disabled={disabled}
              />
            ) : f.tipo === 'select' ? (
              <select aria-label={f.nombre} className={inputCls} value={valor} disabled={disabled}
                onChange={e => setField(f, { valor: e.target.value })}>
                <option value="">Seleccionar…</option>
                {(f.opciones || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.tipo === 'boolean' ? (
              <label className="inline-flex items-center gap-2 text-sm text-content-muted">
                <input type="checkbox" checked={valor === 'true'} disabled={disabled}
                  onChange={e => setField(f, { valor: e.target.checked ? 'true' : 'false' })}
                  className="h-4 w-4 rounded border-divider-strong text-primary focus:ring-primary" />
                {valor === 'true' ? 'Sí' : 'No'}
              </label>
            ) : f.tipo === 'fecha' ? (
              <DatePicker aria-label={f.nombre} value={valor} onChange={(v: string) => setField(f, { valor: v })} />
            ) : (
              <input aria-label={f.nombre}
                type={f.tipo === 'numero' ? 'number' : 'text'}
                className={inputCls} value={valor} disabled={disabled}
                onChange={e => setField(f, { valor: e.target.value })}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
